/**
 * Shadow Ledger Data Model
 * Tracks planned and delivered services to estimate remaining budget
 */

export const createShadowLedger = (data = {}) => {
  return {
    id: data.id || null,
    participantId: data.participantId || null,
    budgetId: data.budgetId || null,
    
    // Budget Information
    apiRemainingBalance: data.apiRemainingBalance || 0, // From Budget API
    lastSyncedAt: data.lastSyncedAt || null,
    
    // Unbilled Delivered Services
    unbilledDeliveredServices: data.unbilledDeliveredServices || [],
    
    // Rostered Future Services
    rosteredFutureServices: data.rosteredFutureServices || [],
    
    // Calculated Estimates
    estimatedBalance: data.estimatedBalance || 0,
    estimatedUnbilledAmount: data.estimatedUnbilledAmount || 0,
    estimatedRosteredAmount: data.estimatedRosteredAmount || 0,
    
    // Alerts
    fundingAtRisk: data.fundingAtRisk ?? false,
    fundingAtRiskThreshold: data.fundingAtRiskThreshold || 1000, // Default $1000
    lastAlertSent: data.lastAlertSent || null,
    
    // Metadata
    createdAt: data.createdAt || new Date().toISOString(),
    updatedAt: data.updatedAt || new Date().toISOString()
  };
};

export const createUnbilledService = (data = {}) => {
  return {
    id: data.id || null,
    serviceDate: data.serviceDate || null,
    serviceType: data.serviceType || null,
    supportItemId: data.supportItemId || null,
    supportItemCode: data.supportItemCode || null,
    description: data.description || '',
    quantity: data.quantity || 1,
    unitPrice: data.unitPrice || 0,
    totalAmount: data.totalAmount || 0,
    staffId: data.staffId || null,
    staffName: data.staffName || null,
    status: data.status || 'delivered', // delivered, billing, billed
    claimId: data.claimId || null,
    createdAt: data.createdAt || new Date().toISOString()
  };
};

export const createRosteredService = (data = {}) => {
  return {
    id: data.id || null,
    serviceDate: data.serviceDate || null,
    serviceTime: data.serviceTime || null,
    serviceType: data.serviceType || null,
    supportItemId: data.supportItemId || null,
    supportItemCode: data.supportItemCode || null,
    description: data.description || '',
    estimatedQuantity: data.estimatedQuantity || 1,
    estimatedUnitPrice: data.estimatedUnitPrice || 0,
    estimatedTotalAmount: data.estimatedTotalAmount || 0,
    staffId: data.staffId || null,
    staffName: data.staffName || null,
    status: data.status || 'scheduled', // scheduled, in-progress, completed, cancelled
    shiftId: data.shiftId || null,
    createdAt: data.createdAt || new Date().toISOString()
  };
};

export const calculateEstimatedBalance = (ledger) => {
  const unbilledAmount = ledger.unbilledDeliveredServices.reduce((sum, service) => {
    return sum + (service.totalAmount || 0);
  }, 0);
  
  const rosteredAmount = ledger.rosteredFutureServices.reduce((sum, service) => {
    return sum + (service.estimatedTotalAmount || 0);
  }, 0);
  
  const estimatedBalance = ledger.apiRemainingBalance - unbilledAmount - rosteredAmount;
  
  return {
    estimatedBalance,
    estimatedUnbilledAmount: unbilledAmount,
    estimatedRosteredAmount: rosteredAmount,
    fundingAtRisk: estimatedBalance < ledger.fundingAtRiskThreshold
  };
};

export const checkFundingAtRisk = (ledger) => {
  const calculations = calculateEstimatedBalance(ledger);
  return calculations.fundingAtRisk;
};

